import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X, Camera } from "lucide-react";
import { toast } from "sonner";
import { useUser } from "@/contexts/UserContext";
import { api } from "@/lib/api";
import PawSpinner from "@/components/PawSpinner";
import PomEmpty from "@/components/PomEmpty";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export default function Characters() {
  const { userId } = useUser();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [selectedChar, setSelectedChar] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(null);

  const { data: characters = [], isLoading } = useQuery({
    queryKey: ["characters", userId],
    queryFn: () => api.listCharacters(userId).then((r) => r.data),
    enabled: !!userId,
  });

  const refreshChar = async (charId) => {
    const { data } = await api.getCharacter(charId);
    setSelectedChar(data);
    queryClient.invalidateQueries({ queryKey: ["characters"] });
  };

  const handleAddPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !selectedChar) return;
    const remaining = 10 - (selectedChar.reference_photos?.length || 0);
    const toUpload = files.slice(0, remaining);
    if (toUpload.length < files.length) {
      toast.error(`Only ${remaining} more photo(s) allowed`);
    }
    setUploading(true);
    let added = 0;
    for (const file of toUpload) {
      try {
        await api.addCharacterPhoto(selectedChar.id, file, userId);
        added++;
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }
    if (added > 0) {
      await refreshChar(selectedChar.id);
      toast.success(`${added} photo${added > 1 ? "s" : ""} added!`);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemovePhoto = async (photoPath) => {
    if (!selectedChar) return;
    setRemoving(photoPath);
    try {
      await api.removeCharacterPhoto(selectedChar.id, photoPath);
      await refreshChar(selectedChar.id);
      toast.success("Photo removed");
    } catch {
      toast.error("Failed to remove photo");
    } finally {
      setRemoving(null);
    }
  };

  if (isLoading)
    return (
      <div className="flex justify-center py-20">
        <PawSpinner />
      </div>
    );

  const photoCount = selectedChar?.reference_photos?.length || 0;

  return (
    <div className="space-y-6 py-2" data-testid="characters-screen">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl text-text-primary">
          Mama's Characters
        </h1>
        <Link
          to="/characters/new"
          className="bg-blush hover:bg-blush-dark text-white px-5 py-2 rounded-pill text-sm font-medium transition-colors flex items-center gap-1.5"
          data-testid="create-character-btn"
        >
          <Plus size={16} /> New
        </Link>
      </div>

      {characters.length > 0 ? (
        <div className="grid grid-cols-2 gap-4">
          {characters.map((char) => (
            <div
              key={char.id}
              onClick={() => setSelectedChar(char)}
              className="bg-white rounded-card shadow-soft p-5 flex flex-col items-center gap-3 cursor-pointer card-hover"
              data-testid={`character-card-${char.id}`}
            >
              <Avatar className="w-20 h-20 border-2 border-border-pink">
                {(char.generated_preview_url ||
                  char.reference_photos?.[0]) && (
                  <AvatarImage
                    src={api.getFileUrl(
                      char.generated_preview_url || char.reference_photos[0]
                    )}
                    alt={char.name}
                  />
                )}
                <AvatarFallback className="bg-cream text-blush text-xl font-heading">
                  {char.name?.[0]}
                </AvatarFallback>
              </Avatar>
              <div className="text-center">
                <p className="font-medium text-text-primary">{char.name}</p>
                <p className="text-xs text-text-secondary mt-0.5">
                  {char.memory_count || 0} memories together
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <PomEmpty message="No characters yet. Create your first one" />
      )}

      {/* Character Detail Modal */}
      <Dialog
        open={!!selectedChar}
        onOpenChange={() => setSelectedChar(null)}
      >
        <DialogContent className="rounded-card bg-white border-border-pink max-w-md mx-4 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-text-primary">
              {selectedChar?.name}
            </DialogTitle>
            <DialogDescription className="text-text-secondary text-sm">
              {selectedChar?.memory_count || 0} memories together
            </DialogDescription>
          </DialogHeader>
          {selectedChar && (
            <div className="space-y-4 mt-2">
              {selectedChar.generated_preview_url && (
                <img
                  src={api.getFileUrl(selectedChar.generated_preview_url)}
                  alt={selectedChar.name}
                  className="w-full rounded-input"
                />
              )}

              {/* Reference photos with add/remove */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-text-secondary font-medium">
                    Reference photos ({photoCount}/10)
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {selectedChar.reference_photos?.map((photo, i) => (
                    <div
                      key={i}
                      className="relative aspect-square rounded-input overflow-hidden group"
                      data-testid={`ref-photo-${i}`}
                    >
                      <img
                        src={api.getFileUrl(photo)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => handleRemovePhoto(photo)}
                        disabled={removing === photo}
                        className="absolute top-1 right-1 w-6 h-6 bg-black/40 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors disabled:opacity-50"
                        data-testid={`remove-ref-photo-${i}`}
                      >
                        {removing === photo ? (
                          <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <X size={12} className="text-white" />
                        )}
                      </button>
                    </div>
                  ))}
                  {photoCount < 10 && (
                    <div
                      onClick={() =>
                        !uploading && fileInputRef.current?.click()
                      }
                      className="aspect-square rounded-input border-2 border-dashed border-border-pink flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-cream/50 transition-colors"
                      data-testid="add-ref-photo-btn"
                    >
                      {uploading ? (
                        <PawSpinner size="sm" />
                      ) : (
                        <>
                          <Camera size={18} className="text-blush" />
                          <span className="text-[10px] text-text-secondary">
                            Add
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleAddPhoto}
                  className="hidden"
                  data-testid="ref-photo-file-input"
                />
              </div>

              <p className="text-xs text-text-secondary">
                Created{" "}
                {new Date(selectedChar.created_at).toLocaleDateString()}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
